import axios from 'axios';
import { useEffect, useState } from 'react';

const PickupHistory = () => {
  const [history, setHistory] = useState([]);
  const [custId, setCustomerId] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 7;

  useEffect(() => {
    const id = sessionStorage.getItem('customer_id');
    if (id) {
      setCustomerId(id);
      axios.get('/auth/getPickupHistoryById', {
        params: { customerId: id }
      })
        .then((response) => {
          setCustomerName(response.data.full_name);
          setCustomerAddress(response.data.address);
        })
        .catch((err) => {
          console.error('Failed to fetch customer details', err);
        });
    }
  }, []);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const response = await axios.get('/auth/pickup-history', {
          params: { customerId: custId }
        });
        setHistory(response.data);
      } catch (err) {
        console.error('Failed to fetch pickup history', err);
      }
    };

    if (custId) fetchHistory();
  }, [custId]);

  const filteredHistory = history.filter(record => {
    const term = searchTerm.toLowerCase();
    return (
      (record.transaction_id && record.transaction_id.toLowerCase().includes(term)) ||
      (record.pickup_status && record.pickup_status.toLowerCase().includes(term)) ||
      (record.city && record.city.toLowerCase().includes(term)) ||
      (record.street_address && record.street_address.toLowerCase().includes(term)) ||
      (record.state && record.state.toLowerCase().includes(term)) ||
      (record.country && record.country.toLowerCase().includes(term))
    );
  });

  // pagination logic
  const indexOfLast = currentPage * recordsPerPage;
  const indexOfFirst = indexOfLast - recordsPerPage;
  const currentRecords = filteredHistory.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredHistory.length / recordsPerPage);

  const BarcodeDropdown = ({ barcode }) => {
    const [open, setOpen] = useState(false);

    if (!barcode) return "-";

    const barcodes = barcode
      .split(",")
      .map(b => b.trim())
      .filter(Boolean);

    // ✅ If only one barcode → show normally
    if (barcodes.length === 1) {
      return <span>{barcodes[0]}</span>;
    }

    return (
      <div className="relative">
        <button
          onClick={() => setOpen(prev => !prev)}
          className="text-blue-600 underline text-xs font-medium"
        >
          {open ? "Hide Barcodes ▲" : "Show Barcodes ▼"}
        </button>

        {open && (
          <div className="mt-2 bg-gray-50 border rounded-md p-2 space-y-1">
            {barcodes.map((code, idx) => (
              <div
                key={idx}
                className="px-2 py-1 bg-white border rounded text-xs"
              >
                {code}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };


  return (
    <div className="relative min-h-screen flex flex-col bg-gray-50">
      <main className="flex-grow pt-6 pb-24 px-4 md:px-10">
        <h1 className="text-center text-3xl font-bold mb-4">
          <span className=" text-[#9cc026]">Pickup </span>History
        </h1>
        <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div>
            <label htmlFor="customerId" className="block text-sm font-medium text-gray-700 mb-1">Customer ID</label>
            <input
              type="text"
              id="customerId"
              value={custId}
              readOnly
              className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-100"
            />
          </div>
          <div>
            <label htmlFor="customerName" className="block text-sm font-medium text-gray-700 mb-1">Customer Name</label>
            <input
              type="text"
              id="customerName"
              value={customerName}
              readOnly
              className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-100"
            />
          </div>
          <div>
            <label htmlFor="customerAddress" className="block text-sm font-medium text-gray-700 mb-1">Customer Address</label>
            <input
              type="text"
              id="customerAddress"
              value={customerAddress}
              readOnly
              className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-100"
            />
          </div>
          <div>
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input
              type="text"
              id="search"
              placeholder="Search history..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-md bg-white"
            />
          </div>
        </section>

        <div className="overflow-x-auto bg-white shadow rounded-lg">
          <table className="min-w-full text-sm text-left border border-gray-200">
            <thead className="bg-gray-100 text-gray-700 sticky top-0 z-10">
              <tr>
                <th className="px-5 py-3 border-b">S.No</th>
                <th className="px-5 py-3 border-b">Pickup Date</th>
                <th className="px-5 py-3 border-b">Pickup Time</th>
                <th className="px-5 py-3 border-b">Status</th>
                <th className="px-5 py-3 border-b">Pickup Address</th>
                <th className="px-5 py-3 border-b">Barcodes</th>
                <th className="px-5 py-3 border-b">Number of Bags</th>
                <th className="px-5 py-3 border-b">Driver Assigned</th>
                <th className="px-5 py-3 border-b">Payment Date</th>
                <th className="px-5 py-3 border-b">Payment Method</th>
                <th className="px-5 py-3 border-b">Amount</th>
              </tr>
            </thead>
            <tbody>
              {currentRecords.length === 0 ? (
                <tr>
                  <td colSpan="11" className="text-center py-6 text-gray-500">
                    No matching records found.
                  </td>
                </tr>
              ) : (
                currentRecords.map((record, index) => (
                  <tr
                    key={index}
                    className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                  >
                    <td className="px-5 py-3 border-b">{indexOfFirst + index + 1}</td>
                    <td className="px-5 py-3 border-b">
                      {record.pickup_date.split('T')[0]}
                    </td>
                    <td className="px-5 py-3 border-b">{record.pickup_time}</td>
                    <td className="px-5 py-3 border-b">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${record.pickup_status === 'completed'
                          ? 'bg-green-100 text-green-700'
                          : record.pickup_status === 'pending'
                            ? 'bg-yellow-100 text-yellow-700'
                            : record.pickup_status === 'cancelled'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-gray-100 text-gray-700'
                          }`}
                      >
                        {record.pickup_status}
                      </span>
                    </td>
                    <td className="px-5 py-3 border-b">
                      {`${record.street_address}, ${record.city}, ${record.state}, ${record.zip}, ${record.country}`}
                    </td>
                    <td className="px-5 py-3 border-b">
                      <BarcodeDropdown barcode={record.barcode} />
                    </td>
                    <td className="px-5 py-3 border-b">{record.estimated_bags}</td>
                    <td className="px-5 py-3 border-b">
                      {record.driver_assigned} - {record.driver_name}
                    </td>
                    <td className="px-5 py-3 border-b">
                      {record.pickup_completed_at
                        ? new Date(record.pickup_completed_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })
                        : '-'}
                    </td>
                    <td className="px-5 py-3 border-b">{record.payment_method || '-'}</td>
                    <td className="px-5 py-3 border-b">{record.net_total != null ? `$${record.net_total}` : '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex justify-center mt-6 gap-2">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(prev => prev - 1)}
              className="px-3 py-1 border rounded-md disabled:opacity-50"
            >
              &lt;
            </button>

            {(() => {
              const pages = [];
              if (totalPages <= 6) {
                for (let i = 1; i <= totalPages; i++) pages.push(i);
              } else {
                if (currentPage <= 3) {
                  pages.push(1, 2, 3, 4, "...", totalPages);
                } else if (currentPage >= totalPages - 2) {
                  pages.push(1, "...", totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
                } else {
                  pages.push(1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages);
                }
              }
              return pages.map((page, index) =>
                page === "..." ? (
                  <span key={index} className="px-3 py-1">...</span>
                ) : (
                  <button
                    key={index}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-1 border rounded-md ${currentPage === page ? "bg-[#a3e635] text-white" : ""}`}
                  >
                    {page}
                  </button>
                )
              );
            })()}

            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(prev => prev + 1)}
              className="px-3 py-1 border rounded-md disabled:opacity-50"
            >
              &gt;
            </button>
          </div>
        )}

      </main>


    </div>
  );
};

export default PickupHistory;
